import { OptionNameValuePair } from "../lib/interfaces/types";
import { CREDIT_FACILITY_LABELS, CreditAccount, CreditFacility } from "./credit_accounts";
import { EntityType, RELATED_AS_LABELS, RelatedAs } from "./stakeholder_relationships";


export const INLINE_ROW_LIMIT = 2;

export const CREDIT_FACILITY_OPTIONS: OptionNameValuePair<string>[] =
  Object.values(CreditFacility).map((value) => ({
    name: CREDIT_FACILITY_LABELS[value],
    value,
  }));

/** Display label for a role, e.g. RelatedAs.SALES_MANAGER -> "Sales Manager". */
export const relatedAsLabel = (value?: RelatedAs): string =>
  value ? RELATED_AS_LABELS[value] : "";

export const EMPLOYEE_ROLES: RelatedAs[] = [
  RelatedAs.EMPLOYEE, RelatedAs.FORMER_EMPLOYEE, RelatedAs.SALES_MANAGER,
  RelatedAs.SANCTIONING_MANAGER, RelatedAs.FIELD_OFFICER, RelatedAs.UNDERWRITER,
  RelatedAs.LEGAL_OFFICER, RelatedAs.RELATIONSHIP_MANAGER, RelatedAs.OTHERS_EMPLOYEE,
];

export const RELATED_AS_BY_ENTITY_TYPE: Record<EntityType, RelatedAs[]> = {
  [EntityType.LOAN_ACCOUNT]: [
    RelatedAs.BORROWER, RelatedAs.PRIMARY_BORROWER, RelatedAs.CO_BORROWER, RelatedAs.GUARANTOR,
    ...EMPLOYEE_ROLES
  ],
  [EntityType.STAKEHOLDER]: [
    RelatedAs.PROPRIETOR, RelatedAs.PARTNER, RelatedAs.DIRECTOR,
    RelatedAs.PROMOTER, RelatedAs.TRUSTEE, RelatedAs.AUTHORIZED_SIGNATORY,
  ],
  [EntityType.ACCOUNT]: [
    RelatedAs.PRIMARY_ACCOUNT_HOLDER,
    RelatedAs.JOINT_ACCOUNT_HOLDER,
    RelatedAs.BENEFICIARY_ACCOUNT_HOLDER,
    RelatedAs.NOMINEE,
    RelatedAs.MANDATE_HOLDER,
    RelatedAs.POWER_OF_ATTORNEY_HOLDER,
    RelatedAs.EMPLOYEE,
    RelatedAs.FORMER_EMPLOYEE,
    RelatedAs.RELATIONSHIP_MANAGER,
    RelatedAs.SALES_MANAGER,
    RelatedAs.BRANCH_MANAGER,
    RelatedAs.KYC_OFFICER,
    RelatedAs.CUSTOMER_SERVICE_OFFICER,
    RelatedAs.TELLER,
    RelatedAs.BUSINESS_CORRESPONDENT,
    RelatedAs.BUSINESS_CORRESPONDENT_AGENT,
    RelatedAs.DRAWER,
    RelatedAs.PAYEE,
    RelatedAs.ATM_CUSTODIAN,
    RelatedAs.CASH_REPLENISHMENT_AGENCY,
    RelatedAs.CHEST_CUSTODIAN,
    RelatedAs.HEAD_CASHIER,
    RelatedAs.IT_STAFF,
    RelatedAs.SYSTEM_ADMIN,
    RelatedAs.THIRD_PARTY_FRAUDSTER,
    RelatedAs.EXTERNAL_ATTACKER,
    RelatedAs.VENDOR,
    RelatedAs.PROPRIETOR,
    RelatedAs.PARTNER,
    RelatedAs.DIRECTOR,
    RelatedAs.PROMOTER,
    RelatedAs.TRUSTEE,
    RelatedAs.AUTHORIZED_SIGNATORY,
    RelatedAs.OTHER,
    RelatedAs.OTHERS_EMPLOYEE,
    RelatedAs.OTHERS_OUTSIDER,
  ],
  [EntityType.CASE]: Object.values(RelatedAs),
};

/** Roles that can link to a loan account */
export const LOAN_ACCOUNT_ROLES: RelatedAs[] = RELATED_AS_BY_ENTITY_TYPE[EntityType.LOAN_ACCOUNT]

/**
 * Roles offered in the employee-type dropdown
 */
export const EMPLOYEE_TYPE_OPTION_ROLES: RelatedAs[] = [
  RelatedAs.FORMER_EMPLOYEE,
  RelatedAs.SALES_MANAGER,
  RelatedAs.SANCTIONING_MANAGER,
  RelatedAs.FIELD_OFFICER,
  RelatedAs.UNDERWRITER,
  RelatedAs.LEGAL_OFFICER,
  RelatedAs.RELATIONSHIP_MANAGER,
  RelatedAs.OTHERS_EMPLOYEE,
];

/**
 * Roles offered in the party-type dropdown
 */
export const PARTY_TYPE_OPTION_ROLES: RelatedAs[] = [
  RelatedAs.CO_BORROWER,
  RelatedAs.GUARANTOR,
  ...EMPLOYEE_TYPE_OPTION_ROLES,
  RelatedAs.OTHERS_OUTSIDER,
];

const toOptions = (roles: RelatedAs[]): OptionNameValuePair<RelatedAs>[] =>
  roles.map((v) => ({ name: relatedAsLabel(v), value: v }));

/** Party-type dropdown options. */
export const buildPartyTypeOptions = (): OptionNameValuePair<RelatedAs>[] =>
  toOptions(PARTY_TYPE_OPTION_ROLES);

/** Employee-type dropdown options. */
export const buildEmployeeTypeOptions = (): OptionNameValuePair<RelatedAs>[] =>
  toOptions(EMPLOYEE_TYPE_OPTION_ROLES);

export interface LoanAccount {
  accountNumber: string;
  extAccountNumber: string;
  product: string;
  creditFacility: string;
  accountName: string;
}

export interface AccountRelations {
  id: string;
  related_as?: RelatedAs;
  account_numbers: string[];
}

export interface PrimaryParty {
  id: string;
  name: string;
  account_relationships: AccountRelations[];
  employee_details?: EmployeeDetails;
}

export interface Employee {
  key: string;
  types: RelatedAs[];
  name: string;
  employeeId?: string;
}

export interface CreateCreditAccountsRequest {
  credit_accounts: CreditAccount[];
}

export interface StakeholderFields {
  name: string;
  is_potential_perpetrator?: boolean;
  is_classified_fraud?: boolean;
  is_ro_applicable?: boolean;
}

export interface StakeholderRelationshipFields {
  entity_type: EntityType;
  entity_id: string;
  related_as: RelatedAs;
}

export interface EmployeeDetails {
  employee_id?: string;
  designation?: string;
}

export interface CreateCaseStakeholderRequest {
  stakeholder: StakeholderFields;
  relationships?: StakeholderRelationshipFields[];
  employee_details?: EmployeeDetails;
}